import { lazy, Suspense, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { newId } from '@os/doc';
import { AuthCallback } from '../features/auth/AuthCallback';
import { LoginPage } from '../features/auth/LoginPage';
import { RequireAuth } from '../features/auth/RequireAuth';
import { Button, buttonClass, cn, focusRing } from '../ui';
import { ToastHost } from '../ui/Toast';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { SignupRedirect } from '../features/auth/SignupRedirect';
import { Dashboard } from '../features/dashboard/Dashboard';
import { InvitePage } from '../features/sharing/InvitePage';
import { SharePage } from '../features/sharing/SharePage';
import type { Api } from '../lib/api';
import { getSupabase } from '../lib/supabase';
import { createSupabaseApi } from '../lib/supabase-api';
import { Providers } from './providers';
import { createQueryClient } from './query-client';
import { matchRoute, useLocation, type RouteMatch } from './router';
import { BoardView } from './routes/BoardRoute';
import { ServerBoardView } from './routes/ServerBoardRoute';
import { UpdateBanner } from '../features/about/UpdateBanner';
import { ReadOnlyService } from '../features/dashboard/ReadOnlyService';
import { resolveTheme } from './theme';
import { systemPrefersDark, ThemeApplier, useTheme } from './theme-store';

/**
 * Internal component gallery (/dev/ui, P16-2): its own chunk, imported only under VITE_DEV_UI=1 so a production build
 * does not contain it at all (`pnpm check:dev-ui`). The flag is compared inline, not through devUiEnabled(): Vite
 * replaces `import.meta.env.VITE_DEV_UI` with a literal and Rollup drops the dead import, a function call it keeps.
 */
const DevUiGallery = import.meta.env.VITE_DEV_UI === '1' ? lazy(() => import('../features/dev-ui/DevUiGallery')) : null;
const gallery = DevUiGallery && (
  <Suspense fallback={null}>
    <DevUiGallery />
  </Suspense>
);

/**
 * "Skip to content" (WCAG 2.4.1): the first Tab stop of every screen, off screen until it has the focus. It moves the focus
 * to the page's main landmark (every screen has one: the dashboard page, the board, the auth card), which then reads
 * as the start of the content; the next Tab reaches its first control, past the sidebar or the top bar.
 */
export function SkipLink() {
  const { t } = useTranslation();
  return (
    <a
      href="#main-content"
      onClick={(e) => {
        e.preventDefault(); // the hash would be read as a route change
        const main = document.querySelector<HTMLElement>('main');
        if (!main) return;
        if (!main.hasAttribute('tabindex')) main.tabIndex = -1; // focusable by script, not a Tab stop
        main.classList.add('outline-none');
        main.focus();
      }}
      className={cn(
        // Parked above the screen until it has the focus (not sr-only: a 1 px link would fail the 44 px touch sweeps).
        'fixed left-4 top-4 mt-(--safe-top) z-(--layer-tooltip) -translate-y-[200%] rounded-full bg-fg px-4 py-2 font-sans text-sm font-medium text-bg shadow-float focus:translate-y-0 pointer-coarse:min-h-11 pointer-coarse:min-w-11',
        focusRing,
      )}
    >
      {t('p18.f.skipToContent')}
    </a>
  );
}

/** Local-only mode (no Supabase configured): the P1 home with local boards. */
function LocalHome() {
  const { t, i18n } = useTranslation();
  const [pref, setPref] = useTheme();
  const [boardId] = useState(() => newId());

  const isFr = i18n.language.startsWith('fr');

  return (
    <main className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center gap-6 px-6">
      <h1 className="font-display text-hero font-normal tracking-display">{t('app.title')}</h1>
      <p className="text-fg-muted">{t('app.subtitle')}</p>
      <div className="flex flex-wrap gap-3">
        <a href={`/b/${boardId}`} className={buttonClass('primary')}>
          {t('home.newBoard')}
        </a>
        <Button onClick={() => i18n.changeLanguage(isFr ? 'en' : 'fr')}>{t('lang.switch')}</Button>
        <Button onClick={() => setPref(resolveTheme(pref, systemPrefersDark()) === 'dark' ? 'light' : 'dark')}>{t('theme.toggle')}</Button>
      </div>
    </main>
  );
}

let defaultApi: Api | null | undefined;

function resolveApi(): Api | null {
  if (defaultApi === undefined) {
    const db = getSupabase();
    defaultApi = db ? createSupabaseApi(db) : null;
  }
  return defaultApi;
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <main className="flex h-dvh items-center justify-center font-sans text-fg-muted">
      <p>{t('app.notFound')}</p>
    </main>
  );
}

/** Screens with their own route; every other route is a page of the dashboard, which fades its pages itself. */
const SCREENS = new Set(['login', 'signup', 'reset', 'callback', 'share', 'devUi', 'invite']);

/**
 * Which screen a route shows (P15c): the routed content fades in when this changes. A board is its own screen, the
 * dashboard one screen (its sidebar stays put); the query string (`?page=`, `?q=`) never counts.
 */
export function routeKey(route: RouteMatch | null): string {
  if (!route) return 'none';
  if (route.name === 'board') return `board:${route.params.boardId}`;
  return SCREENS.has(route.name) ? route.name : 'dashboard';
}

function ConnectedRoutes({ route }: { route: RouteMatch | null }): ReactNode {
  switch (route?.name) {
    case undefined:
      return <NotFound />;
    case 'login':
      return <LoginPage />;
    case 'signup':
      return <SignupRedirect />;
    case 'reset':
      return <ResetPasswordPage />;
    case 'callback':
      return <AuthCallback />;
    case 'share':
      return <SharePage />;
    case 'devUi':
      return gallery;
    case 'invite':
      return <InvitePage />;
    case 'board':
      return (
        <RequireAuth allowAnonymous>
          {/* The editor is the page's main landmark (P12a): screen readers jump to it like to a dashboard page. */}
          <main className="h-full">
            <ServerBoardView boardId={route.params.boardId!} />
          </main>
        </RequireAuth>
      );
    default:
      return (
        <RequireAuth>
          <Dashboard />
        </RequireAuth>
      );
  }
}

/**
 * Without Supabase configuration the app keeps the P1 local mode (home + IndexedDB boards).
 * With it, every page except the auth screens requires a session.
 */
export function App({ api = resolveApi() }: { api?: Api | null }) {
  const { path, search } = useLocation();
  const route = matchRoute(path, search);
  const [queryClient] = useState(createQueryClient);

  if (!api) {
    return (
      <div className="h-dvh">
        <SkipLink />
        {route?.name !== 'devUi' && <ThemeApplier />}
        <div key={routeKey(route)} className="os-route h-full">
          {route?.name === 'devUi' ? (
            gallery
          ) : route?.name === 'board' ? (
            <main className="h-full">
              <BoardView boardId={route.params.boardId!} />
            </main>
          ) : (
            <LocalHome />
          )}
        </div>
        {/* The local mode has no Providers (which mount the toast viewport for every other screen). */}
        <ToastHost />
      </div>
    );
  }
  return (
    <Providers api={api} queryClient={queryClient}>
      <div className="h-dvh">
        <SkipLink />
        {route?.name !== 'devUi' && <ThemeApplier />}
        <div key={routeKey(route)} className="os-route h-full">
          <ConnectedRoutes route={route} />
        </div>
        {/* Only where a Worker serves the app (P5g): the local mode has no /api/health to ask. */}
        {route?.name !== 'devUi' && <UpdateBanner />}
        {/* Supabase refusing writes (P12): over the dashboard and the editor alike. */}
        {route?.name !== 'devUi' && <ReadOnlyService />}
      </div>
    </Providers>
  );
}
